const DEFAULT_TITLE = 'LTS Portal'
const DEFAULT_BODY = 'Test notification'
const DEFAULT_ICON = '/favicon/android-icon-192x192.png'

export type LocalPushNotificationOptions = {
  title?: string
  body?: string
  delayMs?: number
  icon?: string
  tag?: string
}

function assertNotificationSupport(): void {
  if (typeof window === 'undefined') {
    throw new Error('Notifications are only available in the browser')
  }
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications')
  }
  if (!window.isSecureContext) {
    throw new Error('Notifications require a secure context (HTTPS or localhost)')
  }
}

async function ensurePermission(): Promise<NotificationPermission> {
  assertNotificationSupport()
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') {
    throw new Error('Notification permission is blocked. Enable it in browser settings.')
  }
  const result = await Notification.requestPermission()
  if (result !== 'granted') {
    throw new Error('Notification permission was not granted')
  }
  return result
}

async function getActiveServiceWorker(): Promise<ServiceWorker | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return null
    const worker =
      registration.active ||
      registration.waiting ||
      registration.installing ||
      navigator.serviceWorker.controller
    return worker ?? null
  } catch {
    return null
  }
}

function showPageNotification(title: string, options: NotificationOptions): void {
  // Prefer registration.showNotification when a SW exists but isn't messaging;
  // otherwise use the page Notification constructor (dev / no-SW path).
  // eslint-disable-next-line no-new
  new Notification(title, options)
}

/**
 * Show a local OS notification immediately or after delayMs.
 * Prefers the service worker so delayed notifications can fire while the tab is backgrounded.
 * Falls back to the page Notification API when no SW is registered (e.g. development).
 */
export async function sendLocalPushNotification(
  opts: LocalPushNotificationOptions = {}
): Promise<{ via: 'service-worker' | 'page'; delayMs: number }> {
  await ensurePermission()

  const title = opts.title?.trim() || DEFAULT_TITLE
  const delayMs = Number.isFinite(opts.delayMs) ? Math.max(0, Number(opts.delayMs)) : 0
  const notificationOptions: NotificationOptions = {
    body: opts.body?.trim() || DEFAULT_BODY,
    icon: opts.icon || DEFAULT_ICON,
    tag: opts.tag || 'lts-portal-test',
  }

  const worker = await getActiveServiceWorker()
  if (worker) {
    worker.postMessage({
      type: 'SHOW_LOCAL_NOTIFICATION',
      title,
      options: notificationOptions,
      delayMs,
    })
    return { via: 'service-worker', delayMs }
  }

  if (delayMs > 0) {
    window.setTimeout(() => {
      showPageNotification(title, notificationOptions)
    }, delayMs)
  } else {
    showPageNotification(title, notificationOptions)
  }

  return { via: 'page', delayMs }
}
